import { Injectable } from '@angular/core';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { BookingModel } from '@app/core/models';
import { BookingRepository } from '@app/store/booking.repository';
import { LocationRepository } from '@app/store/location.repository';
import { environment } from '@env/environment';
import { TranslocoService } from '@jsverse/transloco';
import {
  Intercom,
  IntercomContent,
  IntercomSpaces,
} from '@unsou/capacitor-intercom';
import { Observable } from 'rxjs';
import { CapacitorBrowserService } from '../capacitor';
import {
  buildBookingChatAttributes,
  buildEmptyBookingChatAttributes,
} from '../chat/booking-attributes';
import { ChatIdentity, ChatProvider } from '../chat/chat-provider';

@Injectable({
  providedIn: 'root',
})
export class IntercomService implements ChatProvider {
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  private bookingSubscription: Subscription | null = null;
  private langSubscription: Subscription | null = null;
  private static readonly MAX_IDENTITY_RETRIES = 3;
  private static readonly IDENTITY_RETRY_DELAY_MS = 2000;

  get unreadCount$(): Observable<number> {
    return this.unreadCountSubject.asObservable();
  }

  constructor(
    private readonly _loggingService: LoggingService,
    private readonly _http: HttpClient,
    private readonly _bookingRepository: BookingRepository,
    private readonly _locationRepository: LocationRepository,
    private readonly _translocoService: TranslocoService,
    private readonly _capacitorBrowserService: CapacitorBrowserService,
  ) {}

  async initialize(user: any, identity?: ChatIdentity) {
    if (this.isInitialized) {
      return;
    }

    // Prevent concurrent initialization attempts (race condition guard)
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.doInitialize(user, identity);
    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async doInitialize(user: any, identity?: ChatIdentity) {
    try {
      // If identity is not provided, try to fetch it with retry
      let actualIdentity = identity;
      if (!actualIdentity) {
        actualIdentity = await this.getIdentityWithRetry();
      }

      if (Capacitor.isNativePlatform()) {
        // Set user hash BEFORE identifying the user.
        // Intercom Identity Verification requires the HMAC to be set prior
        // to login, otherwise subsequent updateUser calls can be silently
        // rejected by Intercom (which is what was causing the "Booking Id"
        // attribute to remain stale after the user switched booking).
        if (actualIdentity?.jwt) {
          await Intercom.setUserHash({ hmac: actualIdentity.jwt });
        }

        // Register user (works for both web and native)
        await Intercom.loginIdentifiedUser({ userId: user.uid });

        // Set app_type for guests
        await Intercom.updateUser({
          customAttributes: {
            app_type: 'guests',
          },
        });
        this.isInitialized = true;
      } else {
        // Web implementation using @unsou/capacitor-intercom load() method
        // Always attempt web initialization — use available user data as fallback
        await this.initializeWeb(user, actualIdentity);
        this.isInitialized = true;
      }

      if (this.isInitialized) {
        LoggingService.log('[Intercom] Successfully initialized');

        // Tear down any subscriptions left over from a previous session
        // (e.g. a logout/login cycle) so we don't end up with multiple
        // listeners racing each other to update Intercom.
        this.langSubscription?.unsubscribe();
        this.bookingSubscription?.unsubscribe();

        // Set up language change subscription after initialization
        this.langSubscription = this._translocoService.langChanges$.subscribe(
          () => {
            Intercom.updateUser({
              languageOverride: this._translocoService.getActiveLang() || 'en',
            }).catch((error) => {
              LoggingService.error('[Intercom] Update user failed', error);
            });
          },
        );

        Intercom.setupUnreadConversationListener().then(() => {
          Intercom.addListener(
            'updateUnreadCount',
            (data: { unreadCount: number }) => {
              this.unreadCountSubject.next(data.unreadCount);
            },
          );
        });

        // Set up active booking subscription after initialization.
        // distinctUntilChanged on `_id` prevents firing duplicate updates
        // when the booking entity reference changes but the booking itself
        // is the same (Intercom Web debounces rapid update calls and was
        // dropping the new booking-id payload on switch).
        this.bookingSubscription = this._bookingRepository.activeBooking$
          .pipe(
            distinctUntilChanged(
              (prev, curr) => (prev?._id ?? null) === (curr?._id ?? null),
            ),
          )
          .subscribe((activeBooking) => {
            const customAttributes = activeBooking
              ? buildBookingChatAttributes(activeBooking)
              : buildEmptyBookingChatAttributes(
                  this._locationRepository.activeLocation,
                );

            LoggingService.log(
              '[Intercom] Updating user with booking attributes',
              customAttributes['Booking Id'] || '(none)',
            );

            Intercom.updateUser({ customAttributes }).catch((error) => {
              LoggingService.error(
                '[Intercom] Update user with booking failed',
                error,
              );
            });
          });
      }
    } catch (error) {
      LoggingService.error('[Intercom] Initialization failed', error);
    }
  }

  private async initializeWeb(user: any, identity?: ChatIdentity) {
    try {
      // Load Intercom for web using @unsou/capacitor-intercom
      // Use identity data when available, fall back to Firebase user data
      await Intercom.load({
        app_id: environment.intercom.appId,
        hide_default_launcher: true,
        user_id: identity?.userId || user?.uid || '',
        intercom_user_jwt: identity?.jwt || '',
        name: user?.displayName || '',
        email: identity?.email || user?.email || '',
        language_override: this._translocoService.getActiveLang() || 'en',
        app_type: 'guests',
      });

      LoggingService.log('[Intercom Web] Loaded successfully');
    } catch (error) {
      LoggingService.error('[Intercom Web Init Error]', error);
      throw error;
    }
  }

  private async getIdentityWithRetry(): Promise<ChatIdentity | undefined> {
    for (
      let attempt = 1;
      attempt <= IntercomService.MAX_IDENTITY_RETRIES;
      attempt++
    ) {
      try {
        const identity = await this.getIdentity();
        if (identity) {
          return identity;
        }
        LoggingService.warn(
          `[Intercom] Identity fetch returned empty (attempt ${attempt}/${IntercomService.MAX_IDENTITY_RETRIES})`,
        );
      } catch (error) {
        LoggingService.warn(
          `[Intercom] Could not fetch identity (attempt ${attempt}/${IntercomService.MAX_IDENTITY_RETRIES})`,
          error,
        );
      }

      if (attempt < IntercomService.MAX_IDENTITY_RETRIES) {
        await new Promise((resolve) =>
          setTimeout(resolve, IntercomService.IDENTITY_RETRY_DELAY_MS),
        );
      }
    }

    LoggingService.warn(
      '[Intercom] All identity fetch attempts failed, continuing without identity verification',
    );
    return undefined;
  }

  displayInAppMessages() {
    Intercom.displayInAppMessages();
  }

  displayHelpCenter() {
    Intercom.present({ space: IntercomSpaces.HelpCenter });
  }

  displayMessageComposer(message: string) {
    Intercom.displayMessageComposer({ message });
  }

  displayArticle(articleId: string) {
    Intercom.displayArticle({ articleId });
  }

  logEvent(name: string, data?: any) {
    Intercom.logEvent({ name, data });
  }

  startSurvey(surveyId: string) {
    Intercom.presentContent({
      contentType: IntercomContent.Survey,
      contentId: surveyId,
    });
  }

  logout() {
    Intercom.logout();
    this.isInitialized = false;
    this.initializationPromise = null;
    this.bookingSubscription?.unsubscribe();
    this.bookingSubscription = null;
    this.langSubscription?.unsubscribe();
    this.langSubscription = null;
    this.unreadCountSubject.next(0);
  }

  // Backward compatibility methods
  async showMessages() {
    Intercom.present({ space: IntercomSpaces.Messages });
  }

  async helpCenter() {
    this.displayHelpCenter();
  }

  async displayMessenger() {
    Intercom.present({ space: 'Messages' });
    /* this.displayInAppMessages(); */
  }

  async newMessage(message?: string) {
    this.displayMessageComposer(message || '');
  }

  async showArticle(articleId: string) {
    this.displayArticle(articleId);
  }

  async trackEvent(eventName: string, metaData?: any) {
    this.logEvent(eventName, metaData);
  }

  async reportAnIssue() {
    this.logEvent('report-a-problem');
  }

  async shutdown() {
    this.logout();
  }

  // Additional helper methods for API integration
  async getIdentity(): Promise<ChatIdentity | undefined> {
    const url = `${environment.api.serverUrl}/intercom/identity`;
    return await firstValueFrom(this._http.get<ChatIdentity>(url));
  }
}
